import { inject, onMounted, onUnmounted, provide, ref, watch, type InjectionKey, type Ref } from 'vue';

interface IDirtyStep {
  isDirty: () => boolean;
  recapture: () => void;
}

interface IDirtyRegistry {
  register: (step: IDirtyStep) => void;
  unregister: (step: IDirtyStep) => void;
}

const dirtyRegistryKey: InjectionKey<IDirtyRegistry> = Symbol('dirtyRegistry');

// Order-independent serialization: arrays and object keys are sorted, so callers can return raw
// selections (e.g. ids in any order) without normalizing themselves.
const canonicalize = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    const items = value.map(canonicalize);
    return items.sort((a, b) => {
      const left = JSON.stringify(a);
      const right = JSON.stringify(b);
      if (left < right) return -1;
      return left > right ? 1 : 0;
    });
  }
  if (value !== null && typeof value === 'object') {
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(value).sort()) {
      sorted[key] = canonicalize((value as Record<string, unknown>)[key]);
    }
    return sorted;
  }
  return value;
};

const serialize = (value: unknown): string => JSON.stringify(canonicalize(value));

/**
 * Dirty-tracking for a dialog. Snapshot the dialog's committed state on open via
 * `captureSnapshot()` and pass `isDirty` to `confirmConfig.showConfirmOn` so the discard-changes
 * prompt only appears on real changes.
 *
 * For multi-step dialogs whose steps buffer their edits locally (and only commit to the parent on
 * "continue"), steps that call `useDirtyStep()` are aggregated automatically: `isDirty()` is true
 * when the dialog's own committed state changed OR any currently-mounted step reports itself dirty.
 * `captureSnapshot()` also re-baselines every registered step, so it is safe to reuse on dialogs
 * that keep steps mounted between opens (e.g. `v-show`) or that re-baseline after an apply action.
 *
 * `getState` returns any JSON-serializable view of the data; arrays are compared order-independently.
 *
 * Pass `currentStep` for a multi-step dialog to treat advancing past the first screen as a user
 * action: the step shown when `captureSnapshot()` runs is remembered as the first screen, and
 * leaving it makes the dialog dirty on its own. This lets later steps drop their per-step dirty
 * tracking — only the first screen still needs a snapshot (via `getState` and/or one `useDirtyStep`).
 */
export const useDirtySnapshot = <T>(getState: () => T, currentStep?: Ref<unknown>) => {
  const baseline = ref<string>('');
  const steps = new Set<IDirtyStep>();
  const firstStep = ref<unknown>(currentStep?.value);
  const hasLeftFirstStep = ref(false);

  if (currentStep) {
    watch(currentStep, (step) => {
      if (step !== firstStep.value) hasLeftFirstStep.value = true;
    });
  }

  // Call once the committed state is established (e.g. at the end of show()).
  const captureSnapshot = (): void => {
    baseline.value = serialize(getState());
    steps.forEach((step) => step.recapture());
    firstStep.value = currentStep?.value;
    hasLeftFirstStep.value = false;
  };

  const isDirty = (): boolean =>
    hasLeftFirstStep.value || baseline.value !== serialize(getState()) || [...steps].some((step) => step.isDirty());

  provide(dirtyRegistryKey, {
    register: (step) => steps.add(step),
    unregister: (step) => steps.delete(step),
  });

  return { captureSnapshot, isDirty };
};

/**
 * Dirty-tracking for one step of a multi-step dialog whose live edits are buffered locally (not yet
 * committed to the parent). Captures its baseline on mount and registers with the nearest
 * `useDirtySnapshot()` ancestor so the dialog's confirm prompt reflects in-progress step edits and so
 * `captureSnapshot()` can re-baseline it.
 *
 * `getState` returns any JSON-serializable view of the step's buffer; arrays are compared
 * order-independently.
 */
export const useDirtyStep = <T>(getState: () => T) => {
  const baseline = ref<string>('');
  const recapture = (): void => {
    baseline.value = serialize(getState());
  };
  const isDirty = (): boolean => baseline.value !== serialize(getState());
  const registry = inject(dirtyRegistryKey, null);
  const step: IDirtyStep = { isDirty, recapture };

  onMounted(() => {
    recapture();
    registry?.register(step);
  });
  onUnmounted(() => registry?.unregister(step));

  return { isDirty };
};
